import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useHasPlayFlowLayerQuery } from '@/state/queries/flows/use-get-flow';

export type FlowPlanModeHeaderLabels = 'slotAssets' | 'schedule';

const LABEL_KEYS = {
  slotAssets: {
    draft: 'flows.detail.slotAssets.listModeDraft',
    playCue: 'flows.detail.slotAssets.listModePlayCue',
  },
  schedule: {
    draft: 'flows.detail.editDetails.scheduleHeaderDraft',
    playCue: 'flows.detail.editDetails.scheduleHeaderPlayCue',
  },
} as const satisfies Record<FlowPlanModeHeaderLabels, { draft: string; playCue: string }>;

/** Shared live/PLAY detection + error|secondary accent; schedule label follows live context, slot assets follow PLAY layer. */
export function useFlowPlanModeHeaderState(
  flowId: string | undefined,
  labels: FlowPlanModeHeaderLabels = 'slotAssets',
) {
  const { t } = useTranslation();
  const flow = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const { isSuccess: hasPlayFlowLayer, refetch: refetchPlayFlowLayer } = useHasPlayFlowLayerQuery(flowId);

  const isLiveContextRunning = Boolean(
    flow?.liveContext?.isRunning || flowPlan?.liveContext?.isRunning || flowOrganize?.liveContext?.isRunning,
  );
  const hasPlayModeDoc = hasPlayFlowLayer || isLiveContextRunning;

  useEffect(() => {
    if (isLiveContextRunning) {
      void refetchPlayFlowLayer();
    }
  }, [isLiveContextRunning, refetchPlayFlowLayer]);

  const keys = LABEL_KEYS[labels];
  // Both schedule and slot-assets PLAN toggle: live/running → play cue; otherwise draft cue.
  // Do not key off "PLAY layer exists" — a PLAN document must stay labeled draft even after a
  // PLAY copy has been created.
  const showPlayCueLabel = isLiveContextRunning;
  const label = showPlayCueLabel ? t(keys.playCue) : t(keys.draft);
  const color = isLiveContextRunning ? ('error' as const) : ('secondary' as const);

  return { label, color, isLiveContextRunning, hasPlayModeDoc };
}
